import { Box, Button, Grid, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { ActiveStatusLabel, TargetTypeLabel } from '../../../components/Curation/Labels';
import { DateComponent, SubmitControl } from '../../../components/SubmitControl';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import { deleteUserSegmentAbtest, saveUserSegmentCurationAbtest, updateUserSegmentAbtest } from '../../../services/api/Abtest';
import {
  IAbTestVariant,
  IAbTestVariantData,
  ICollection,
  IFeaturedCard,
  ISegmentAbtestUpdateRequestDto,
  ISegmentCurationAbTest,
  ISegmentCurationAbTestRequest,
  ISegmentCurationCollection,
  ISegmentCurationFeaturedCard,
  ISegmentCurationType,
  IUserSegment,
} from '../../../services/types';
import { isFeaturedCard } from '../../../services/utils';
import { RouteNames } from '../../config';
import ObjectSelector from './ObjectSelector';
import useStyles from './styles';

interface IProps {
  targetCuration: ISegmentCurationFeaturedCard | ISegmentCurationCollection;
  mode: 'create' | 'edit' | 'view';
  segment: IUserSegment;
  onClose: () => void;
  onSave: () => void;
}

export const AbtestView = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();
  const path = RouteNames.UserSegment;

  const { targetCuration } = props;

  // states: data
  const [mode, setMode] = useState<'create' | 'edit' | 'view'>(props.mode);
  const [abTest, setAbtest] = useState<ISegmentCurationAbTest | undefined>(targetCuration?.abTests?.[0]);
  const [startsAt, setStartsAt] = useState<Date | null>(abTest && abTest.startsAt ? abTest.startsAt : null);
  const [endsAt, setEndsAt] = useState<Date | null>(abTest && abTest.endsAt ? abTest.endsAt : null);
  const [testTarget, setTestTarget] = useState<any>();
  const [variants, setVariants] = useState<IAbTestVariant[]>(
    targetCuration && targetCuration.abTests!.length > 0 ? targetCuration.abTests![0].variants! : [],
  );
  const setStatesFromData = (data?: ISegmentCurationAbTest) => {};

  // states
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  // refs

  // handlers
  const addNewVariant = () => {
    if (variants && variants.length >= 4) {
      return false;
    }
    const newVariant: IAbTestVariant = {
      abTestId: abTest?.id,
      data: {
        type: isFeaturedCard(targetCuration.type) ? 'featured_card' : 'collection',
      },
    };
    if (variants.length !== 0) {
      // create에서 자동으로 추가한 경우에는 change 없음으로
      setHasChanges(true);
    }
    setVariants([...variants, newVariant]);
  };
  const onChangeDate = (date: Date, func: React.Dispatch<React.SetStateAction<Date | null>>) => {
    func(date);
    setHasChanges(true);
  };
  const onAddObject = (obj: IFeaturedCard | ICollection, idx: number) => {
    const newData: IAbTestVariantData = isFeaturedCard(targetCuration.type)
      ? { type: 'featured_card', id: obj.id! }
      : { type: 'collection', trackingId: (obj as ICollection).trackingId! };
    const newVariant: IAbTestVariant = { ...variants[idx], object: obj, data: newData };
    const newVariants = [...variants];
    newVariants[idx] = newVariant;
    setVariants(newVariants);
    setHasChanges(true);
  };
  const onDeleteVariantObject = (idx: number) => {
    const newVariant = { ...variants[idx] };
    delete newVariant.object;
    delete newVariant.data;
    const newVariants = [...variants];
    newVariants[idx] = newVariant;
    setVariants(newVariants);
    setHasChanges(true);
  };
  const onClickSubmit = () => {
    const payload: ISegmentCurationAbTestRequest = {};
    if (abTest) {
      payload.id = abTest.id;
    }
    if (mode === 'create' || abTest!.startsAt !== startsAt || abTest!.endsAt !== endsAt) {
      payload.abTest = { startsAt: startsAt as Date, endsAt: endsAt as Date };
    }
    payload.variants = variants
      .filter(variant => variant.data !== undefined && (variant.data.id !== undefined || variant.data.trackingId !== undefined))
      .map((variant: IAbTestVariant) => {
        return {
          id: variant.id,
          data: { type: variant.data!.type, id: variant.data!.id, trackingId: variant.data!.trackingId },
        };
      });

    saveAbTest(payload);
  };
  const onClickCancel = () => {
    if (hasChanges) {
      confirm.open({
        title: 'Changes will be lost',
        text: 'If you leave or close this page, any changes you made will be lost. Are you sure you want to continue?',
        onProceed: props.onClose,
      });
    } else {
      props.onClose();
    }
  };
  const onClickCompleted = () => {
    const now = moment();
    confirm.open({
      title: 'Status change',
      text: now.isBefore(startsAt!)
        ? `This will cancel the scheduled A/B test. Are you sure you want to proceed? `
        : `You cannot resume this test once it's completed. Are you sure you want to complete the test?`,
      onProceed: () => {
        updateAbTest({
          endsAt: now.toDate(),
          startsAt: now.isSameOrBefore(startsAt!) ? now.add(-10, 'seconds').toDate() : startsAt!,
        });
      },
    });
  };
  const onClickActivated = () => {
    confirm.open({
      title: 'Status change',
      text: 'Are you sure you want to start this A/B test now?',
      onProceed: () => {
        updateAbTest({ startsAt: new Date(), endsAt: endsAt! });
      },
    });
  };
  const onClickDelete = () => {
    confirm.open({
      title: 'Delete',
      text: 'Are you sure you want to delete this A/B test now?',
      onProceed: () => {
        deleteUserSegmentAbtest(abTest!.id!)
          .then(() => {
            props.onSave();
          })
          .catch(e => {
            console.log(e);
            snackbar.error(e);
          });
      },
    });
  };

  // API

  // watches
  const validateForm = () => {
    const validateMessages = [];
    if (!startsAt) {
      validateMessages.push('Start date must not be empty.');
    }
    if (!endsAt) {
      validateMessages.push('End date must not be empty.');
    }
    if (startsAt && endsAt && moment(startsAt).isSameOrAfter(endsAt)) {
      validateMessages.push('Start date must be before end date.');
    }
    if (
      variants.filter(variant => variant.data !== undefined && (variant.data.id !== undefined || variant.data.trackingId !== undefined)).length === 0
    ) {
      validateMessages.push('Add variant.');
    }
    setValidationMessages(validateMessages);
  };
  const saveAbTest = (payload: ISegmentCurationAbTestRequest) => {
    saveUserSegmentCurationAbtest(targetCuration.id!, payload)
      .then(() => {
        props.onSave();
      })
      .catch(e => {
        console.log(e);
        snackbar.error(e);
      });
  };
  const updateAbTest = (data: ISegmentAbtestUpdateRequestDto) => {
    updateUserSegmentAbtest(targetCuration.abTests![0].id!, data)
      .then(() => {
        props.onSave();
      })
      .catch(e => {
        console.log(e);
        snackbar.error(e);
      });
  };

  // watches
  useEffect(() => {
    validateForm();
  }, [startsAt, endsAt, variants]);

  // init
  if (variants.length === 0) {
    addNewVariant();
  }

  return (
    <Box className={classes.abtestModalRoot}>
      <Paper className={classes.modalPaper}>
        {mode !== 'create' && abTest && (
          <Box className={classes.titleWrap} display="flex" alignItems="center" paddingRight={'30px'}>
            <ActiveStatusLabel startsAt={abTest.startsAt || null} endsAt={abTest.endsAt || null} margin={8} />
            <Box flexGrow={1}>
              <Typography component="h6" variant="subtitle1" style={{ fontWeight: 500 }}>
                {targetCuration.object.title}
              </Typography>
            </Box>
            {mode === 'view' && (
              <Box flexBasis="auto">
                {moment().isBefore(abTest.startsAt) && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disableElevation
                    className={classes.normalizedButton}
                    onClick={onClickActivated}
                  >
                    Activate
                  </Button>
                )}
                {(abTest.endsAt === null || moment().isBefore(abTest.endsAt)) && (
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    className={classes.normalizedButton}
                    onClick={onClickCompleted}
                    style={{ marginLeft: 10 }}
                  >
                    Complete
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}
        {mode === 'create' && (
          <Box className={classes.titleWrap}>
            <Typography component="h6" variant="subtitle1" style={{ fontWeight: 500 }}>
              Create A/B Test
            </Typography>
          </Box>
        )}
        <IconButton className={classes.modalCloseButton} onClick={onClickCancel} style={mode === 'edit' ? { marginTop: '5px' } : {}}>
          <CloseIcon style={{ color: '#bebebe' }} />
        </IconButton>
        <Box className={classes.variantsListScroll}>
          <Box display="flex" marginTop="29px">
            <Box width="143px" className={classes.abtestMidtitle}>
              A/B Test Id : {abTest ? abTest.id! : '-'}
            </Box>
            <Box className={classes.abtestMidtitle}>
              <span>Segment</span>
              <span style={{ fontSize: 14, fontWeight: 'normal', color: '#888888', marginLeft: '13px' }}>{props.segment.name}</span>
            </Box>
          </Box>
          <Box marginTop="25px" fontSize="16px" fontWeight="500">
            Test Period
          </Box>
          <Box display="flex">
            <Box marginRight={'30px'}>
              <DateComponent
                label="Start"
                readonly={mode === 'view'}
                dateValue={startsAt}
                onChange={newDate => {
                  onChangeDate(newDate, setStartsAt);
                }}
              />
            </Box>
            <Box>
              <DateComponent
                label="End"
                readonly={mode === 'view'}
                dateValue={endsAt}
                onChange={newDate => {
                  onChangeDate(newDate, setEndsAt);
                }}
              />
            </Box>
          </Box>
          <Box marginTop="25px">
            <Box fontSize="16px" fontWeight="500">
              Test Target
            </Box>
            {isFeaturedCard(targetCuration.type) && (
              <Box display="flex" marginTop="12px">
                <Box width={'98px'} marginRight={'15px'}>
                  <img
                    src={`${process.env.CDN_URL}/${(targetCuration.object as IFeaturedCard).displayCoverUrl}?w=240`}
                    className={classes.coverImage}
                  />
                </Box>
                <Box>
                  <Box>
                    <TargetTypeLabel type={targetCuration.object.type} />
                  </Box>
                  <Box marginTop="6px">
                    <div className={classes.twolineDots} style={{ width: 300 }}>
                      {targetCuration.object.title}
                    </div>
                  </Box>
                </Box>
              </Box>
            )}
            {targetCuration.type === ISegmentCurationType.FictionSerials && (
              <Box marginTop="12px">
                <Box display="flex">
                  <Box style={{ width: 300 }}>{targetCuration.object.title}</Box>
                </Box>
              </Box>
            )}
          </Box>
          {mode !== 'view' && (!variants || variants?.length < 4) && (
            <Box className={classes.addVariantWrap}>
              <Button variant="contained" size="medium" onClick={addNewVariant} disableElevation className={classes.addVariantButton}>
                <AddIcon style={{ color: '#4051b5', fontSize: '20px', marginRight: '6px' }} />
                Add another variant
              </Button>
            </Box>
          )}
          <Box>
            {variants?.map((variant, idx) => (
              <Box key={`abtest-variant-item-${idx}`} className={classes.abtesVariantWrap}>
                <Box className={classes.abtestVariantTitle}>
                  <Typography className={classes.abtestMidtitle}>
                    Variant {String.fromCharCode(65 + idx)} (Variant ID : {variant.id || '-'})
                  </Typography>
                </Box>
                <Grid container className={`${classes.dualTableWrap} ${classes.dualTableWrap16}`} style={{ minHeight: 139 }}>
                  <Grid item className={mode === 'view' ? classes.dualTableLeftWrapExpand : classes.dualTableLeftWrap} style={{ minHeight: 139 }}>
                    <Table>
                      <TableHead>
                        <TableRow className={classes.dualTableLeftHeader}>
                          {isFeaturedCard(targetCuration.type) && (
                            <TableCell align="center" width="100">
                              Cover
                            </TableCell>
                          )}
                          <TableCell>Title</TableCell>
                          {!isFeaturedCard(targetCuration.type) && (
                            <TableCell align="center" width="100">
                              Stories
                            </TableCell>
                          )}
                          <TableCell align="center" width="48">
                            Delete
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {variant.object && (
                          <TableRow key={`abtest-variant-item`} className={classes.noBorderRow}>
                            {isFeaturedCard(targetCuration.type) && (
                              <TableCell align="center">
                                <div
                                  style={{
                                    backgroundImage: `url(${process.env.CDN_URL}/${(variant.object as IFeaturedCard).displayCoverUrl ||
                                      (variant.object as IFeaturedCard).coverUrl}?w=320)`,
                                  }}
                                  className={classes.coverImage}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              {isFeaturedCard(targetCuration.type) && (
                                <div>
                                  <TargetTypeLabel type={variant.object!.type} />
                                </div>
                              )}
                              <div>{(variant.object as IFeaturedCard).title}</div>
                            </TableCell>
                            {!isFeaturedCard(targetCuration.type) && (
                              <TableCell align="center" width="100">
                                {(variant.object as ICollection).storyCount!}
                              </TableCell>
                            )}
                            <TableCell align="center">
                              <IconButton
                                onClick={() => {
                                  onDeleteVariantObject(idx);
                                }}
                                disabled={mode === 'view'}
                                color={mode === 'view' ? 'default' : 'primary'}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Grid>
                  {mode !== 'view' && (
                    <Grid item className={classes.dualTableRightWrap} style={{ flexBasis: 320 }}>
                      <Box className={classes.dualTableRightBox} style={{ height: 188 }}>
                        <Box className={classes.dualTableRightHeader} style={{ position: 'relative', zIndex: 1 }}>
                          <Typography component="span" className={classes.dualTableRightHeaderText}>
                            Add {isFeaturedCard(targetCuration.type) ? 'Featured Card' : 'Collection'}
                          </Typography>
                        </Box>
                        <ObjectSelector
                          type={isFeaturedCard(targetCuration.type) ? 'featuredcard' : 'collection'}
                          selectLimit={1}
                          className={classes.dualTableRightContent}
                          selectedIds={variant.object ? [variant.object.id!] : []}
                          onAddObject={obj => onAddObject(obj, idx)}
                        />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Box>
        </Box>
        <Box>
          <SubmitControl
            mode={mode}
            onDeleteClick={mode === 'view' && endsAt && moment().isSameOrAfter(endsAt) ? onClickDelete : undefined}
            onEditClick={
              mode === 'view' && (abTest!.endsAt === null || moment().isBefore(abTest!.endsAt))
                ? () => {
                    setMode('edit');
                  }
                : undefined
            }
            onCancelClick={onClickCancel}
            onSubmitClick={onClickSubmit}
            onViewCancelClick={onClickCancel}
            validationMessages={validationMessages}
            hideIcon={true}
          />
        </Box>
      </Paper>
    </Box>
  );
};
